import { useEffect, useState } from "react";
import { useLoaderData } from "react-router-dom";

import FunctionalButton from "components/layout/FunctionalButton";
import MapCanvas from "components/layout/MapCanvas";
import SkeletonImage from "components/layout/loading/SkeletonImage";
import Spinner from "components/layout/loading/Spinner";

import { delay } from "lib/utils/utils";

import { ReactComponent as ViewIcon } from "assets/svgs/View.svg";

import { DUMMY_MISSION_LOGS_INFO } from "lib/consts/testing";

const logTabNames = [
    "Log Overview",
    "Inspection Result",
    "Background Monitoring",
    "Video Replay",
    "Media",
];

const logOverviewEventColumnHeaderNames = ["timestamp", "event", "data", "View"];

const MissionBrief = ({ data, className = "" }) => {
    return (
        <div className={`flex h-full flex-col justify-start ${className}`}>
            {Object.keys(data).map((key, id) => (
                <p key={id} className="text-h5l capitalize md:text-h5s tablet:text-h5m">
                    {key}: {data[key] || "Nill"}
                </p>
            ))}
        </div>
    );
};

const CheckpointBrief = ({ brief, inspections, className = "" }) => {
    return (
        <div className={`flex h-full flex-col justify-start ${className}`}>
            {Object.keys(brief).map((key, id) => (
                <p key={id} className="text-h5l capitalize md:text-h5s tablet:text-h5m">
                    {key}: {brief[key] || "Nill"}
                </p>
            ))}
            {inspections && (
                <div className="flex flex-col justify-start">
                    <p className="text-h5l capitalize md:text-h5s tablet:text-h5m">Inspections</p>
                </div>
            )}
        </div>
    );
};

const LogOverviewDetailPanel = ({ logs = [] }) => {
    const logEventsClassName =
        "w-[calc((100%-3.125rem)/3)] text-h5l text-white md:text-h5s tablet:text-h5m";

    return (
        <div className="flex w-full flex-col justify-start space-y-4 rounded-r10 bg-black90 p-4">
            <p className="text-h4l text-white md:text-h4s tablet:text-h4m">Log Overview</p>
            <div className="w-full rounded-r5 border border-black70">
                <div className="flex h-8 w-full  items-center justify-between  bg-black70 p-2">
                    {logOverviewEventColumnHeaderNames.map((columnName, id) => (
                        <p
                            key={id}
                            className={`text-h5l capitalize text-white md:text-h5s tablet:text-h5m ${
                                columnName !==
                                logOverviewEventColumnHeaderNames[
                                    logOverviewEventColumnHeaderNames.length - 1
                                ]
                                    ? "w-[calc((100%-3.125rem)/3)]"
                                    : "w-[3.125rem]"
                            }`}
                        >
                            {columnName}
                        </p>
                    ))}
                </div>
                {logs.length > 0 ? (
                    <div className="flex w-full flex-col justify-start divide-y divide-black70">
                        {logs.map((log, id) => (
                            <div
                                key={id}
                                className="flex w-full  items-center space-x-2 p-2 text-h5l text-white md:text-h5s tablet:text-h5m "
                            >
                                <p className={logEventsClassName}>{log.timestamp}</p>
                                <p className={logEventsClassName}>{log.event}</p>
                                {log.data !== undefined ? (
                                    (() => {
                                        switch (log.data.type) {
                                            case "image":
                                                return (
                                                    <span className={logEventsClassName}>
                                                        <img
                                                            className="max-h-[0.75rem]"
                                                            src={require(`assets/imgs/testings/${log.data.result}`)}
                                                            alt="Log Event's Result"
                                                        />
                                                    </span>
                                                );
                                            default:
                                                return (
                                                    <p className={logEventsClassName}>
                                                        {log.data.result}
                                                    </p>
                                                );
                                        }
                                    })()
                                ) : (
                                    <span className={logEventsClassName} />
                                )}
                                <FunctionalButton
                                    icon={<ViewIcon className="text-black40" />}
                                    className="h-full w-[3.125rem]"
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex items-center justify-center">No logs...</div>
                )}
            </div>
        </div>
    );
};

const ViewLogPage = () => {
    const { ARC, logID } = useLoaderData();

    const [logDetail, setLogDetail] = useState({});
    const [logMap, setLogMap] = useState("");
    const [logTab, setLogTab] = useState(logTabNames[0]);
    const [selectedCheckpoint, setSelectedCheckpoint] = useState();

    const [isLoading, setIsLoading] = useState(true);
    const [isMapLoading, setIsMapLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            await delay(500);

            return DUMMY_MISSION_LOGS_INFO.filter((log) => log.ARC === ARC && log.id === logID)[0];
        };

        fetchData()
            .then((response) => {
                setLogDetail(response);
            })
            .catch((e) => console.log(e))
            .finally(() => {
                setIsLoading(false);
            });

        const fetchMapData = async () => {
            setIsMapLoading(true);
            await delay(2000);

            return "Map.png";
        };

        fetchMapData()
            .then((response) => {
                setLogMap(response);
            })
            .catch((e) => console.log(e))
            .finally(() => {
                setIsMapLoading(false);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debug
    useEffect(() => {
        if (logMap !== "") console.log(require("assets/imgs/testings/Map.png"));
    });

    return (
        <div className="scroll-vertical pr-1">
            {isLoading ? (
                <Spinner />
            ) : (
                <>
                    {logDetail !== undefined ? (
                        <>
                            <div className="flex w-full flex-col justify-start space-y-6">
                                <div className="flex h-6  items-center justify-between">
                                    <p className="text-h3l font-bold md:text-h3s tablet:text-h3m">
                                        {logDetail.mission}
                                    </p>
                                    <FunctionalButton
                                        title="Export CSV"
                                        titleClassName="text-h4l tablet:text-h4m md:text-h4s"
                                    />
                                </div>
                                <div className="flex min-h-[15rem] w-full  items-center justify-between space-x-3 rounded-r10 bg-black90 p-4">
                                    <div className="flex h-[24rem] w-2/4 flex-col justify-start space-y-1">
                                        <MissionBrief
                                            data={{
                                                Station: logDetail.station || "Nil",
                                                "Performed ARC": logDetail.ARC,
                                                Payload: logDetail.ARCPayload || "Nil",
                                                "Performed robot": logDetail.robot,
                                                Map: logDetail.map || "Nil",
                                                "Start Time": logDetail.time,
                                                "End Time": logDetail.time,
                                                "Mission Length": logDetail.time,
                                                "Mission Progress": logDetail.state,
                                                "GPS Location": logDetail.gps || "Nil",
                                                "Control User": logDetail.user || "Andy Chan",
                                            }}
                                        />
                                        <CheckpointBrief
                                            brief={{
                                                "Checkpoint Name": "Checkpoint 1",
                                                "Create Time": logDetail.time,
                                                "Last Edit Time": "Nil",
                                                Status: "OK",
                                            }}
                                        />
                                    </div>
                                    <div className="h-full w-2/4">
                                        {isMapLoading || logMap === undefined ? (
                                            <SkeletonImage />
                                        ) : (
                                            <MapCanvas
                                                mapName={logMap}
                                                className="h-full max-w-full"
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="flex w-full flex-col justify-start space-y-6">
                                    <div className="flex h-[2.125rem] w-full  items-center justify-start space-x-6 border-b border-black80">
                                        {logTabNames.map((tab) => (
                                            <p
                                                className={`text-h4 w-fit cursor-pointer py-2 hover:text-primary md:text-h4s tablet:text-h4m ${
                                                    logTab === tab
                                                        ? "border-b-2 border-primary pt-1 text-primary"
                                                        : "text-black60"
                                                }`}
                                                onClick={() => setLogTab(tab)}
                                            >
                                                {tab}
                                            </p>
                                        ))}
                                    </div>
                                    {(() => {
                                        switch (logTab) {
                                            default:
                                                return (
                                                    <LogOverviewDetailPanel
                                                        logs={logDetail.events}
                                                    />
                                                );
                                        }
                                    })()}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex h-full flex-col items-center justify-center">
                            Cannot get log - {logID} from {ARC}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ViewLogPage;
